import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LinkIcon from '@mui/icons-material/Link';
import { darken, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { ContactSearchMethods, SelectorIds } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  contactSearchMethod: ContactSearchMethods | '';
  productGroup: ProductGroups;

  handleContactSearchMethodChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

/**
 * The KI search is the default way to fill a campaign. The other sources are expert
 * tools: they stay folded away until the user deliberately opens them.
 */
const SearchMethod: React.FC<Props> = ({ contactSearchMethod, handleContactSearchMethodChange, productGroup }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  // Helper function to handle product-specific translations
  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  const isExpertMethod = !!contactSearchMethod && contactSearchMethod !== ContactSearchMethods.WE_SEARCH;
  const [open, setOpen] = useState(isExpertMethod);
  const isOpen = open || isExpertMethod;

  const selectMethod = (method: ContactSearchMethods) => () => {
    handleContactSearchMethodChange({ target: { value: method } } as React.ChangeEvent<HTMLInputElement>);
  };

  const options = [
    {
      method: ContactSearchMethods.OWN_SEARCH,
      icon: <LinkIcon />,
      title: getLocalizedText('ownLinkedinSearch'),
      description: t('expertOwnSearchDescription'),
    },
    {
      method: ContactSearchMethods.UPLOAD_CONTACT_LIST,
      icon: <FileUploadOutlinedIcon />,
      title: getLocalizedText('uploadContactList'),
      description: t('expertUploadDescription'),
    },
    {
      method: ContactSearchMethods.ADD_FROM_CAMPAIGN,
      icon: <GroupsOutlinedIcon />,
      title: t('addContactsFromCampaignMethodTitle'),
      description: t('expertFromCampaignDescription'),
    },
  ];

  return (
    <section id={SelectorIds.SEARCH_METHOD} className={classes.section}>
      {isExpertMethod ? (
        <PaiLink
          text={t('backToKiSearch')}
          variant={'primary'}
          size={'md'}
          bold
          IconComponent={<ArrowBackIcon fontSize="small" />}
          iconPlacement={'start'}
          action={selectMethod(ContactSearchMethods.WE_SEARCH)}
        />
      ) : (
        <PaiLink
          text={getLocalizedText('expertMethodsToggle')}
          variant={'primary'}
          size={'md'}
          bold
          IconComponent={isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          iconPlacement={'end'}
          action={() => setOpen((value) => !value)}
        />
      )}

      {isOpen ? (
        <>
          <Typography className={classes.greyText} mt={'8px'} mb={'16px'}>
            {t('expertMethodsIntro')}
          </Typography>
          <div className={classes.options}>
            {options.map((option) => {
              const selected = contactSearchMethod === option.method;
              return (
                <button
                  key={option.method}
                  type="button"
                  className={cx(classes.option, selected ? 'checked' : undefined)}
                  onClick={selectMethod(option.method)}
                  aria-pressed={selected}
                >
                  <span className={classes.optionHead}>
                    <span className={classes.optionIcon}>{option.icon}</span>
                    <span className={classes.optionTitle}>{option.title}</span>
                    <span className={cx(classes.checkbox, selected ? 'checked' : undefined)} />
                  </span>
                  <span className={classes.optionDescription}>{option.description}</span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  );
};

const useStyles = makeStyles()((theme) => ({
  section: {
    position: 'relative',
    padding: '24px 48px',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  options: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'left',
    padding: 0,
    cursor: 'pointer',
    background: '#FFF',
    borderRadius: '6px',
    border: `1px solid ${theme.palette.greys.light}`,
    fontFamily: 'inherit',
    '&:hover': {
      background: darken('#FFF', 0.05),
    },
    '&.checked': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  optionHead: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    paddingRight: 'calc(16px + 20px + 16px)',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  optionIcon: {
    display: 'flex',
    color: theme.palette.primary.main,
    '& svg': {
      fontSize: theme.typography.pxToRem(28),
    },
  },
  optionTitle: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  checkbox: {
    position: 'absolute',
    top: '50%',
    right: '16px',
    transform: 'translateY(-50%)',
    width: '20px',
    height: '20px',
    boxSizing: 'border-box',
    borderRadius: '4px',
    border: `2px solid ${theme.palette.greys.light}`,
    '&.checked': {
      border: `2px solid ${theme.palette.primary.main}`,
      background: theme.palette.primary.main,
    },
  },
  optionDescription: {
    padding: '16px',
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.4,
    color: theme.palette.greys.medium,
  },
}));

export default SearchMethod;
